/**
 * MijnBoxspring — Swatch tap-to-swap (v3)
 *
 * Approach: when the user taps a swatch, intercept the click, parse the
 * variant ID from the swatch's href, fetch the product JSON from Shopify,
 * find the matching variant's featured_image, and swap the main card image
 * to that URL. The swatch link's href is preserved so tapping the *image*
 * after a swatch tap still navigates to the right variant page.
 *
 * Why fetch instead of preload: this theme does not render hidden variant
 * images alongside the swatches, so there is nothing to swap from. Fetching
 * is fast (typically <150ms) and avoids bloating the collection page with
 * preloaded variant images for every product.
 *
 * Caching: each product's JSON is fetched once per session and cached in
 * memory, so the second tap on the same product is instant.
 */
(function () {
  'use strict';

  var SELECTORS = {
    card: '.product-card',
    swatch: 'a.swatch-color',
    mainImg: '.product-card__image--main img',
    secondaryImg: '.product-card__image--second img',
    imageWrapperLink: '.product-card__image-wrapper > a',
  };

  // In-memory cache: handle -> Promise<productData>
  var productCache = {};

  function getProductData(handle) {
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetch('/products/' + handle + '.js', {
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        // On failure, clear the cache so a retry can happen
        delete productCache[handle];
        throw err;
      });

    return productCache[handle];
  }

  /**
   * Parse the variant ID and product handle from a swatch's href.
   * Example: /products/boxspring-met-opbergruimte-sierra?variant=56597973533056
   */
  function parseSwatchHref(href) {
    try {
      var url = new URL(href, window.location.origin);
      var match = url.pathname.match(/\/products\/([^\/]+)/);
      if (!match) return null;
      var variantId = url.searchParams.get('variant');
      if (!variantId) return null;
      return { handle: match[1], variantId: parseInt(variantId, 10) };
    } catch (e) {
      return null;
    }
  }

  function findVariantImage(productData, variantId) {
    if (!productData || !productData.variants) return null;
    var variant = productData.variants.find(function (v) {
      return v.id === variantId;
    });
    if (!variant) return null;

    // Prefer the variant's own featured_image if set
    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    // Fall back to looking up the variant's image_id in product.images
    if (variant.image_id && productData.images) {
      var imgObj = productData.media && productData.media.find(function (m) {
        return m.id === variant.image_id;
      });
      if (imgObj && imgObj.src) return imgObj.src;
    }
    return null;
  }

  /**
   * Build a Shopify CDN URL with a width parameter, mirroring how the original
   * srcset is structured. Shopify CDN supports ?width= on any image URL.
   */
  function buildSizedUrl(baseUrl, width) {
    // Strip existing width query if present, then add new one
    var url = baseUrl.split('?')[0];
    return url + '?width=' + width;
  }

  function swapImage(mainImage, newSrc) {
    if (!mainImage || !newSrc) return;

    // Add a quick fade for visual smoothness
    mainImage.style.transition = 'opacity 0.15s ease';
    mainImage.style.opacity = '0.4';

    var preload = new Image();
    preload.onload = function () {
      // Build a srcset matching the existing one's widths
      var widths = [355, 450, 535, 710, 900, 1070, 1420];
      var srcset = widths
        .map(function (w) {
          return buildSizedUrl(newSrc, w) + ' ' + w + 'w';
        })
        .join(', ');

      mainImage.src = buildSizedUrl(newSrc, 900);
      mainImage.srcset = srcset;
      mainImage.style.opacity = '1';
    };
    preload.onerror = function () {
      // Restore opacity if the image failed to load
      mainImage.style.opacity = '1';
    };
    preload.src = buildSizedUrl(newSrc, 900);
  }

  function handleClick(e) {
    var swatch = e.target.closest(SELECTORS.swatch);
    if (!swatch) return;

    var card = swatch.closest(SELECTORS.card);
    if (!card) return;

    // Only intercept on collection-page-style cards (not all .product-card uses)
    if (!card.classList.contains('mb-card-refined')) return;

    var parsed = parseSwatchHref(swatch.getAttribute('href'));
    if (!parsed) return; // can't parse, let link work normally

    e.preventDefault();
    e.stopPropagation();

    // Update selected visual state immediately (don't wait for fetch)
    card.querySelectorAll('a.swatch-color[aria-selected="true"]').forEach(function (el) {
      el.removeAttribute('aria-selected');
    });
    swatch.setAttribute('aria-selected', 'true');

    // Update product link href so tapping the image goes to the right variant
    card.querySelectorAll(SELECTORS.imageWrapperLink).forEach(function (a) {
      a.href = swatch.getAttribute('href');
    });

    var mainImage = card.querySelector(SELECTORS.mainImg);
    if (!mainImage) return;

    // Fetch the product data and swap the image
    getProductData(parsed.handle)
      .then(function (productData) {
        var newSrc = findVariantImage(productData, parsed.variantId);
        if (newSrc) {
          // Ensure URL has https: prefix (Shopify URLs sometimes start with //)
          if (newSrc.indexOf('//') === 0) newSrc = 'https:' + newSrc;
          swapImage(mainImage, newSrc);
        }
      })
      .catch(function () {
        // Silent failure — selected state still updates, link still works
      });
  }

  /**
   * When the user navigates back from a product page, the browser may restore
   * the page from bfcache with a previously-swapped image still showing.
   * Force images to revalidate so the original src renders correctly.
   */
  function handlePageShow(e) {
    if (!e.persisted) return;
    document.querySelectorAll(SELECTORS.mainImg).forEach(function (img) {
      var src = img.src;
      if (src) {
        img.src = '';
        img.src = src;
      }
    });
  }

  function init() {
    document.removeEventListener('click', handleClick, true);
    document.addEventListener('click', handleClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('collection:loaded', init);
  window.addEventListener('pageshow', handlePageShow);
})();
